import React, { useEffect, useState, useRef } from 'react';
import { Loader2, Download, X, FileText, ExternalLink, Smartphone } from 'lucide-react';

/**
 * Detecta si el usuario está en un dispositivo móvil donde el iframe con PDF
 * no funciona de forma confiable (iOS Safari, Android Chrome).
 * Combina user-agent + tamaño de pantalla para mayor precisión.
 */
const esMobile = () => {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isMobileUA = /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  const isSmallScreen = window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
  return isMobileUA || isSmallScreen;
};

/**
 * Modal de previsualización de PDF con soporte para múltiples tabs.
 *
 * Props:
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - title: string
 *  - subtitle?: string
 *  - tabs: Array<{
 *      key: string,
 *      label: string,
 *      loadBlobUrl: () => Promise<string | { url, filename }>,
 *      onDownload?: () => Promise<void>
 *    }>
 *  - defaultTab?: string
 *
 * Performance: Los PDFs de cada tab se cachean en memoria durante la vida
 * del modal. Si el usuario cambia de tab y regresa, el PDF ya cargado se
 * muestra instantáneamente (no se regenera).
 */
const PdfPreviewModal = ({ isOpen, onClose, title, subtitle, tabs = [], defaultTab }) => {
  // Arranca SIN tab activo: el usuario debe hacer clic en uno para que
  // se genere ese PDF. Solo se preselecciona si el caller pasó defaultTab.
  const [activeKey, setActiveKey] = useState(defaultTab || '');
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // Progreso "fake" pero útil durante la generación del PDF: avanza con curva
  // ease-out hasta 95% en ~tiempo estimado; salta a 100% cuando termina de
  // verdad. El tiempo estimado se aprende del historial guardado en
  // localStorage para que sea cada vez más realista.
  const [progreso, setProgreso] = useState(0);
  // Glow temporal en el botón "Descargar" del header cuando termina la
  // generación. Ayuda al usuario a no usar el botón de descarga del visor
  // de PDF interno (que guarda con nombre genérico UUID en lugar del
  // nombre real que IMEC genera).
  const [resaltarDescarga, setResaltarDescarga] = useState(false);
  // Cache: { [tabKey]: { url, filename, error } }
  const [cache, setCache] = useState({});
  // Ref con los URLs blob creados para poder revocarlos al cerrar
  const createdUrlsRef = useRef([]);
  // Detectar mobile (iframe con PDF no funciona bien en iOS Safari / Android Chrome)
  const [isMobile, setIsMobile] = useState(false);

  // Re-detectar si cambia el tamaño de pantalla (rotación, etc.)
  useEffect(() => {
    const check = () => setIsMobile(esMobile());
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Cuando el modal abre con un único tab, auto-seleccionarlo. No tiene sentido
  // pedirle al usuario que "elija" entre una sola opción — el modal arranca
  // generando ese PDF directamente (caso típico: Post Cuenta con un solo IFPC).
  useEffect(() => {
    if (isOpen && !activeKey && tabs.length === 1) {
      setActiveKey(tabs[0].key);
    }
  }, [isOpen, activeKey, tabs]);

  const activeTab = tabs.find((t) => t.key === activeKey);
  const activeCache = cache[activeKey];
  const url = activeCache?.url;
  const filename = activeCache?.filename;
  const error = activeCache?.error;

  // Extraer mensaje de error de una respuesta axios/fetch
  const extraerMensajeError = (e) => {
    const backendDetail = e?.response?.data?.detail;
    if (backendDetail) return String(backendDetail);
    // Si la respuesta es un blob con JSON, intentar parsearlo
    if (e?.response?.data instanceof Blob) {
      return 'No se pudo cargar la vista previa del PDF';
    }
    return e?.message || 'No se pudo cargar la vista previa del PDF';
  };

  // Helper: leer/guardar tiempo estimado de generación por tab key en
  // localStorage. Permite que la barra de progreso sea cada vez más realista.
  const tiempoEstimado = (key) => {
    try {
      const v = parseInt(localStorage.getItem(`pdf_gen_ms_${key}`) || '0', 10);
      // Default 4s si nunca se ha medido; clamp 1.5s–15s
      return Math.min(15000, Math.max(1500, v || 4000));
    } catch { return 4000; }
  };

  const guardarTiempo = (key, ms) => {
    try {
      // Suaviza: promedio del valor anterior y el nuevo (evita picos)
      const anterior = parseInt(localStorage.getItem(`pdf_gen_ms_${key}`) || '0', 10);
      const nuevo = anterior > 0 ? Math.round((anterior + ms) / 2) : ms;
      localStorage.setItem(`pdf_gen_ms_${key}`, String(nuevo));
    } catch { /* ignore */ }
  };

  // Cargar PDF del tab activo (solo si no está cacheado)
  useEffect(() => {
    if (!isOpen || !activeTab) return;

    // Si ya tenemos cache para este tab (url o error), no re-cargar
    if (cache[activeKey]) return;

    let cancelled = false;
    const tInicio = Date.now();
    setLoading(true);

    activeTab
      .loadBlobUrl()
      .then((result) => {
        if (cancelled) {
          const urlToRevoke = typeof result === 'string' ? result : result?.url;
          if (urlToRevoke) URL.revokeObjectURL(urlToRevoke);
          return;
        }
        // Recordar cuánto tardó esta generación (para la próxima vez)
        guardarTiempo(activeKey, Date.now() - tInicio);
        const newUrl = typeof result === 'string' ? result : result?.url;
        const newFilename = typeof result === 'string' ? null : result?.filename;
        if (newUrl) createdUrlsRef.current.push(newUrl);
        setCache((prev) => ({
          ...prev,
          [activeKey]: { url: newUrl, filename: newFilename, error: null },
        }));
      })
      .catch(async (e) => {
        if (cancelled) return;
        // Si el error viene de axios con responseType blob, parsear el blob como JSON
        let msg = extraerMensajeError(e);
        if (e?.response?.data instanceof Blob && e.response.data.type === 'application/json') {
          try {
            const text = await e.response.data.text();
            const parsed = JSON.parse(text);
            if (parsed?.detail) msg = parsed.detail;
          } catch (err2) { /* ignore */ }
        }
        setCache((prev) => ({
          ...prev,
          [activeKey]: { url: null, filename: null, error: msg },
        }));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeKey, isOpen, activeTab, cache]);

  // Animar barra de progreso mientras carga. Curva ease-out que va hasta 95%
  // en el tiempo estimado; cuando termina el loading real, salta a 100%.
  useEffect(() => {
    if (!loading) {
      // Si veníamos cargando, terminar en 100% por un instante antes de resetear
      setProgreso(p => (p > 0 ? 100 : 0));
      const t = setTimeout(() => setProgreso(0), 400);
      return () => clearTimeout(t);
    }
    setProgreso(0);
    const start = Date.now();
    const objetivo = tiempoEstimado(activeKey);
    const interval = setInterval(() => {
      const transcurrido = Date.now() - start;
      const t = Math.min(1, transcurrido / objetivo);
      // Ease-out cubic: empieza rápido, se desacelera al final
      const eased = 1 - Math.pow(1 - t, 3);
      const pct = Math.min(95, Math.round(eased * 95));
      setProgreso(pct);
    }, 100);
    return () => clearInterval(interval);
  }, [loading, activeKey]);

  // Cuando termina de cargar un PDF (loading false + url disponible),
  // resaltar el botón Descargar del header durante ~4s para guiar al
  // usuario y que no use la descarga interna del visor de PDF (que
  // guarda con un nombre genérico UUID en lugar del nombre correcto).
  useEffect(() => {
    if (!loading && url) {
      setResaltarDescarga(true);
      const t = setTimeout(() => setResaltarDescarga(false), 4000);
      return () => clearTimeout(t);
    }
    setResaltarDescarga(false);
  }, [loading, url]);

  // Tecla Escape y bloquear scroll del body
  useEffect(() => {
    if (!isOpen) return;

    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  // Al cerrar el modal, revocar todos los blob URLs creados
  useEffect(() => {
    if (isOpen) return;
    // Modal cerrado: limpiar cache y revocar URLs
    createdUrlsRef.current.forEach((u) => {
      try { URL.revokeObjectURL(u); } catch (e) { /* ignore */ }
    });
    createdUrlsRef.current = [];
    setCache({});
    setActiveKey(defaultTab || '');
  }, [isOpen]);

  if (!isOpen) return null;

  const handleDownload = async () => {
    if (!activeTab) return;
    try {
      setDownloading(true);
      // Fast path: si ya tenemos el blob URL cargado y conocemos el filename,
      // reusar el blob en memoria en vez de regenerar el PDF
      if (url && filename) {
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else if (activeTab.onDownload) {
        await activeTab.onDownload();
      }
    } catch (e) {
      /* noop - el error se mostrará en la UI */
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="h-5 w-5 text-blue-600 shrink-0" />
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-gray-900 truncate">{title}</h2>
              {subtitle && <p className="text-xs text-gray-500 truncate">{subtitle}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownload}
              disabled={downloading || loading || !!error || !url}
              title={resaltarDescarga
                ? '⬇️ Descarga aquí para conservar el nombre correcto del archivo'
                : 'Descargar PDF'}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-all duration-300 ${
                resaltarDescarga
                  ? 'ring-4 ring-emerald-300 shadow-[0_0_20px_rgba(16,185,129,0.7)] scale-110 animate-pulse'
                  : ''
              }`}
            >
              {downloading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Descargar
            </button>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Cerrar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Tabs (solo si hay más de 1) */}
        {tabs.length > 1 && (
          <div className="flex gap-2 px-4 sm:px-6 py-2 border-b border-gray-200 bg-gray-50 overflow-x-auto shrink-0">
            {tabs.map((t) => {
              const tabCache = cache[t.key];
              const isLoaded = tabCache && tabCache.url;
              const hasError = tabCache && tabCache.error;
              return (
                <button
                  key={t.key}
                  onClick={() => setActiveKey(t.key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                    activeKey === t.key
                      ? 'bg-blue-600 text-white shadow'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                  title={isLoaded ? 'Ya cargado en caché' : hasError ? 'Error al cargar' : ''}
                >
                  {t.label}
                  {isLoaded && activeKey !== t.key && (
                    <span className="text-green-500 text-[10px]">●</span>
                  )}
                  {hasError && activeKey !== t.key && (
                    <span className="text-red-500 text-[10px]">●</span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-hidden bg-gray-100">
          {!activeKey && !loading && (
            <div className="flex flex-col items-center justify-center h-full text-center px-6 py-10 gap-3">
              <FileText className="h-14 w-14 text-blue-400" />
              <h3 className="text-lg font-semibold text-gray-800">
                Elige qué documento quieres ver
              </h3>
              <p className="text-sm text-gray-500 max-w-md">
                Selecciona una de las opciones de arriba para generar y previsualizar el PDF.
                No se generan archivos hasta que tú lo decidas.
              </p>
            </div>
          )}
          {activeKey && loading && (
            <div className="flex flex-col items-center justify-center h-full px-6">
              <Loader2 className="h-10 w-10 animate-spin text-blue-600 mb-4" />
              <div className="text-gray-700 font-medium mb-3">Generando PDF…</div>
              {/* Barra de progreso animada con curva ease-out */}
              <div className="w-full max-w-sm bg-gray-200 rounded-full h-2.5 overflow-hidden shadow-inner">
                <div
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 h-full rounded-full transition-all duration-200 ease-out"
                  style={{ width: `${progreso}%` }}
                />
              </div>
              <div className="flex items-baseline gap-2 mt-2">
                <span className="text-sm font-mono font-semibold text-blue-700">{progreso}%</span>
                {progreso >= 95 && (
                  <span className="text-xs text-gray-500 italic">terminando los últimos detalles…</span>
                )}
              </div>
            </div>
          )}
          {error && !loading && (
            <div className="flex flex-col items-center justify-center h-full text-center px-6">
              <p className="text-red-600 font-medium max-w-lg">{error}</p>
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => {
                    // Reintentar: quitar del cache para que el useEffect vuelva a cargar
                    setCache((prev) => {
                      const next = { ...prev };
                      delete next[activeKey];
                      return next;
                    });
                  }}
                  className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                >
                  Reintentar
                </button>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cerrar
                </button>
              </div>
            </div>
          )}
          {url && !loading && !error && !isMobile && (
            <iframe src={url} title={title} className="w-full h-full border-0" />
          )}
          {url && !loading && !error && isMobile && (
            <div className="flex flex-col items-center justify-center h-full text-center px-6 py-10 bg-gradient-to-b from-blue-50 to-white">
              <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md border border-blue-100">
                <div className="flex items-center justify-center w-20 h-20 mx-auto mb-4 rounded-full bg-blue-100">
                  <FileText className="h-10 w-10 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">PDF listo para ver</h3>
                <p className="text-sm text-gray-600 mb-2">
                  {filename || 'Documento PDF'}
                </p>
                <p className="text-xs text-gray-500 mb-6 flex items-center justify-center gap-1">
                  <Smartphone className="h-3.5 w-3.5" />
                  La vista previa inline no está disponible en móvil
                </p>
                <div className="flex flex-col gap-3">
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 text-base font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 shadow-md transition-all active:scale-95"
                  >
                    <ExternalLink className="h-5 w-5" />
                    Abrir PDF
                  </a>
                  <button
                    onClick={handleDownload}
                    disabled={downloading}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 text-base font-semibold text-white bg-green-600 rounded-xl hover:bg-green-700 shadow-md transition-all active:scale-95 disabled:opacity-50"
                  >
                    {downloading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <Download className="h-5 w-5" />
                    )}
                    Descargar
                  </button>
                </div>
                <p className="text-[11px] text-gray-400 mt-4 leading-relaxed">
                  Al tocar "Abrir PDF" se abrirá en el visor nativo de tu dispositivo (Safari, Chrome, Adobe Reader, etc.)
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PdfPreviewModal;
